import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { WorkforceCashPolicy } from './workforce-cash.models';
import { WorkforceCashService } from './workforce-cash.service';
import { WorkforceFinanceCurrencyService } from './workforce-finance-currency.service';

@Component({
  selector: 'app-workforce-cash-policy-form', standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, CheckboxModule, DialogModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule],
  templateUrl: './workforce-cash-policy-form.component.html',
  styleUrls: ['./workforce-cash-policy-form.component.css']
})
export class WorkforceCashPolicyFormComponent {
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() completed = new EventEmitter<WorkforceCashPolicy>();
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(WorkforceCashService);
  private readonly session = inject(SessionStorageService);
  private readonly currencies = inject(WorkforceFinanceCurrencyService);
  readonly workerTypes = [
    { label: 'Drivers', value: 'DRIVER' },
    { label: 'Field workers', value: 'FIELD_WORKER' },
    { label: 'Laborers', value: 'LABORER' },
    { label: 'Employees', value: 'HR_EMPLOYEE' }
  ];
  readonly currencyOptions$ = this.currencies.currencyOptions$;
  saving = false;
  error = '';
  readonly form = this.fb.group({
    policyName: ['', [Validators.required, Validators.maxLength(150)]],
    workerType: ['DRIVER' as string | null],
    currencyCode: ['SAR', Validators.required],
    maxTransactionAmount: [null as number | null, [Validators.required, Validators.min(0.01)]],
    maxOpenBalance: [null as number | null, [Validators.required, Validators.min(0.01)]],
    receiptThreshold: [1, [Validators.required, Validators.min(0)]],
    secondApprovalThreshold: [null as number | null, Validators.min(0.01)],
    workLinkRequired: [true],
    active: [true]
  });

  close(): void { if (!this.saving) this.visibleChange.emit(false); }

  save(): void {
    if (this.saving) return;
    this.error = '';
    const companyId = this.session.getCompany()?.id;
    const policyName = this.form.controls.policyName.value?.trim();
    if (!companyId) { this.error = 'Select a company before saving a cash policy.'; return; }
    if (this.form.invalid || !policyName) {
      this.form.markAllAsTouched();
      this.error = 'Complete the required policy fields with valid amounts.';
      return;
    }
    this.saving = true;
    const value = this.form.getRawValue();
    this.service.savePolicy({ policyName, owningCompanyId: companyId,
      workerType: value.workerType || undefined, currencyCode: value.currencyCode!,
      maxTransactionAmount: value.maxTransactionAmount!, maxOpenBalance: value.maxOpenBalance!,
      receiptThreshold: value.receiptThreshold!, secondApprovalThreshold: value.secondApprovalThreshold ?? undefined,
      workLinkRequired: !!value.workLinkRequired, active: !!value.active }).subscribe({
      next: policy => { this.saving = false; this.completed.emit(policy); },
      error: err => { this.saving = false; this.error = err?.error?.message || 'The cash policy could not be saved.'; }
    });
  }
}
